import {useMemo, useState} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {toast} from 'react-toastify'
import {useCognitoAuth} from '@/app/modules/auth'
import {toastDefaultConfig} from '@/shared/config/toastConfig'
import {getLogger} from '@/shared/utils'
import {CustomModal} from '../CustomModal'
import {FileTable, PickedFile} from '../FileTable'
import {BrowserRoot} from '../fileBrowserRoot'
import {folderListQuery} from '../../core/storage/fileStore'
import {createOwnStore} from '../../core/storage/ownStore'
import {OWN_ROOT} from '../../core/storage/types'
import {freeFileName} from './freeFileName'
import './file-picker.scss'

export interface SaveToCloudDialogProps {
  /** Suggested name, editable in the dialog. */
  fileName: string
  content: string
  contentType: string
  onClose: () => void
}

const SHARED_DESTINATION_NOTE = "Saving into folders shared with you isn't supported yet."

/**
 * Saves a text file into a folder of My Files that the user browses to.
 *
 * The body is the Files panel's `FileTable` in pick mode with folders as the answer, as the wizard
 * dialog (`FilePickerHost`) uses it for files. The destination is the highlighted folder, else the
 * folder on screen. An existing file of the same name is kept: the new one gets a `_2`, `_3`… suffix.
 */
export function SaveToCloudDialog({
  fileName,
  content,
  contentType,
  onClose,
}: SaveToCloudDialogProps) {
  const {currentUser} = useCognitoAuth()
  const identityId = currentUser?.identity_id
  const queryClient = useQueryClient()
  const [name, setName] = useState(fileName)
  const [selected, setSelected] = useState<PickedFile>()
  const [location, setLocation] = useState<{segments: string[]; root: BrowserRoot}>()
  const [saving, setSaving] = useState(false)

  // `undefined` when the destination is somewhere a file cannot be saved.
  const folder = useMemo(() => {
    if (selected?.isDir) {
      return selected.share ? undefined : selected.key.replace(/\/$/, '')
    }
    return location?.root.kind === 'own' ? location.segments.slice(1).join('/') : undefined
  }, [selected, location])

  const trimmedName = name.trim()
  const nameError = trimmedName.includes('/') ? 'The name cannot contain "/".' : undefined

  const pickOptions = useMemo(
    () => ({
      selectFolders: true,
      onSelect: (file: PickedFile | undefined) => setSelected(file?.isDir ? file : undefined),
      onConfirm: () => undefined,
    }),
    []
  )

  const save = async () => {
    if (folder === undefined || !identityId || !trimmedName || nameError) {
      return
    }
    setSaving(true)
    const prefix = folder ? `${folder}/` : ''
    try {
      const listing = await queryClient.fetchQuery({
        ...folderListQuery(OWN_ROOT, identityId, folder),
        staleTime: 0,
      })
      const taken = new Set((listing ?? []).map((item) => item.file))
      const finalName = freeFileName(trimmedName, (candidate) => taken.has(`${prefix}${candidate}`))

      await createOwnStore(identityId).upload(
        `${prefix}${finalName}`,
        new File([content], finalName, {type: contentType})
      )

      const renamed =
        finalName !== trimmedName ? ` (a file named ${trimmedName} already existed)` : ''
      toast.success(`Saved as cloud:${prefix}${finalName}${renamed}`, toastDefaultConfig)
      onClose()
    } catch (e) {
      getLogger().error('Could not save the file to the cloud', e)
      toast.error('Could not save the file to the cloud.', toastDefaultConfig)
      setSaving(false)
    }
  }

  // Only said when something blocks the save; the destination itself shows in the breadcrumb.
  const saveHint = nameError ?? (folder === undefined ? SHARED_DESTINATION_NOTE : undefined)

  return (
    <CustomModal
      title='Save to Cloud Files'
      titleIcon='fas fa-cloud-arrow-up'
      dialogClassName='ob-file-pick-modal__dialog'
      closeModal={onClose}
      buttons={[
        {text: 'Cancel', className: 'btn btn-light', onClick: onClose},
        {
          text: saving ? 'Saving…' : 'Save',
          className: 'btn btn-primary',
          disabled: saving || folder === undefined || !trimmedName || !!nameError || !identityId,
          onClick: () => void save(),
          dataTestId: 'omics-save-to-cloud-confirm',
        },
      ]}
    >
      <div className='ob-file-pick'>
        <FileTable
          title='Save to Cloud Files'
          hideTitle
          pick={pickOptions}
          onFolderPathChange={(segments, root) => setLocation({segments, root})}
        />
        <div className='ob-file-pick__save-row'>
          <label htmlFor='ob-save-to-cloud-name'>File name</label>
          <input
            id='ob-save-to-cloud-name'
            type='text'
            className='form-control form-control-sm'
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void save()
            }}
            data-testid='omics-save-to-cloud-name'
          />
        </div>
        {saveHint && <div className='ob-file-pick__save-hint text-muted'>{saveHint}</div>}
      </div>
    </CustomModal>
  )
}
